import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PricesService,
  type Price,
  type PriceCreate,
  type PriceUpdate,
} from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

/** System-wide prices — one rate for every customer. See backend prices module. */
export const usePrices = () =>
  useQuery({
    queryKey: ["prices"],
    queryFn: () => fetchAll<Price>(PricesService.getApiPrices),
    staleTime: 5 * 60_000,
  });

/**
 * Rows are normally seeded, but production only runs migrations — so a rate
 * code ships with no row and this is the only way to give it one.
 */
export const useCreatePrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: PriceCreate) => {
      const res = await PricesService.postApiPrices({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Price;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prices"] });
      qc.invalidateQueries({ queryKey: ["dashboard-chart"] });
      qc.invalidateQueries({ queryKey: ["crm-period-stats"] });
    },
  });
};

export const useUpdatePrice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PriceUpdate }) => {
      const res = await PricesService.putApiPricesById({
        path: { id },
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Price;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prices"] });
      // The B2B economics dashboard reads the analysis rate from here, and
      // caches chart runs for a minute — without this an edit looks ignored.
      qc.invalidateQueries({ queryKey: ["dashboard-chart"] });
      qc.invalidateQueries({ queryKey: ["crm-period-stats"] });
    },
  });
};
